import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import {
  useCustomer,
  usePurchaseOrder,
  usePurchaseOrderStore,
  useWmsItem,
} from "hooks";
import { PurchaseOrder, PurchaseOrderItem, PurchaseOrderQuery } from "types";
import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const parseNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }
    return trimmed
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const getUnitString = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object") {
    return value.name ?? value.code ?? "";
  }
  return "";
};

const sanitizeItems = (
  items?: PurchaseOrderItem[],
  wmsMap?: Map<number | string, any>,
) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const matched =
            wmsMap?.get(item.product_id as any) ||
            wmsMap?.get(item.unit_id as any) ||
            wmsMap?.get(String(item.product_id ?? "")) ||
            wmsMap?.get(String(item.unit_id ?? ""));
          const unitLabel =
            getUnitString((matched as any)?.unit) ||
            (matched?.unit_id ? String(matched.unit_id) : "");
          const quantity = parseNumber(item.quantity);
          const unitPrice = parseNumber(item.unit_price);
          const amount = parseNumber(
            (item as any).amount !== undefined
              ? (item as any).amount
              : quantity * unitPrice,
          );
          const vatRate =
            item.vat_rate !== undefined && item.vat_rate !== null
              ? Number(item.vat_rate)
              : undefined;
          const vatAmount =
            (item as any).vat_amount !== undefined
              ? parseNumber((item as any).vat_amount)
              : vatRate !== undefined && Number.isFinite(vatRate)
                ? (amount * vatRate) / 100
                : undefined;

          return {
            ...(item.product_id ? { product_id: item.product_id } : {}),
            product_name: item.product_name
              ? String(item.product_name).trim()
              : matched?.name
                ? String(matched.name).trim()
                : undefined,
            ...(item.unit_id
              ? { unit_id: item.unit_id }
              : matched?.unit_id
                ? { unit_id: matched.unit_id }
                : {}),
            quantity,
            unit_price: unitPrice,
            amount,
            vat_rate: vatRate,
            ...(vatAmount !== undefined ? { vat_amount: vatAmount } : {}),
            description: item.description
              ? String(item.description).trim()
              : undefined,
            ...(unitLabel ? { unit_label: unitLabel } : {}),
          };
        })
        .filter(
          (item) =>
            Number(item.quantity ?? 0) > 0 ||
            Number(item.unit_price ?? 0) > 0 ||
            Number(item.amount ?? 0) > 0 ||
            Boolean(item.product_id) ||
            Boolean(item.product_name) ||
            Boolean(item.description),
        )
    : [];

const withItemFields = (
  order: PurchaseOrder,
  wmsMap?: Map<number | string, any>,
): PurchaseOrder => {
  const normalizedItems = sanitizeItems(order.items, wmsMap);
  const totalItemsAmount = normalizedItems.reduce(
    (sum, item) => sum + parseNumber(item.amount),
    0,
  );

  return {
    ...order,
    attachments: normalizeAttachments(order.attachments).join(", "),
    items: normalizedItems,
    total_amount:
      order.total_amount !== undefined && order.total_amount !== null
        ? order.total_amount
        : totalItemsAmount,
  };
};

const PurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<PurchaseOrderQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<PurchaseOrder | null>(null);
  const [selectedItems, setSelectedItems] = useState<PurchaseOrderItem[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  const { rows, setRows, removeRow, isLoading, setLoading } =
    usePurchaseOrderStore();

  const { fetchList, deletePurchaseOrder } = usePurchaseOrder();
  const { fetchList: fetchWmsItems } = useWmsItem();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const { data: wmsItems = [] } = fetchWmsItems(true);
  const { data: purchaseOrderData, isFetching } = fetchList(queryParams, true);

  const wmsItemById = useMemo(() => {
    const map = new Map<number | string, any>();
    (wmsItems ?? []).forEach((item: any) => {
      map.set(item.id, item);
      map.set(String(item.id), item);
      if (item.wms_id !== undefined && item.wms_id !== null) {
        map.set(item.wms_id, item);
        map.set(String(item.wms_id), item);
      }
    });
    return map;
  }, [wmsItems]);

  useEffect(() => {
    if (purchaseOrderData?.data) {
      const mapped =
        (purchaseOrderData.data as PurchaseOrder[])?.map((item) =>
          withItemFields(item, wmsItemById),
        ) ?? [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedLabel(
          mapped[0].purchase_code ??
            mapped[0].supplier_name ??
            `Chứng từ ${mapped[0].id}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedLabel("");
      }
    }
  }, [purchaseOrderData, setRows, wmsItemById]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deletePurchaseOrder.isPending));
  }, [deletePurchaseOrder.isPending, isFetching, setLoading]);

  const supplierLabelById = useMemo(() => {
    const map = new Map<number | string, string>();
    (customersData?.data as any[])?.forEach((c) => {
      const label = `${c.code}${c.name ? ` - ${c.name}` : ""}`;
      map.set(c.id, label);
    });
    return map;
  }, [customersData]);

  const supplierAddressById = useMemo(() => {
    const map = new Map<number | string, string>();
    (customersData?.data as any[])?.forEach((c) => {
      if (c.address) {
        map.set(c.id, c.address);
      }
    });
    return map;
  }, [customersData]);

  const columns = useMemo<ColDef<PurchaseOrder>[]>(() => {
    return [
      {
        headerName: "Số chứng từ",
        field: "purchase_code",
        minWidth: 150,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Ngày chứng từ",
        field: "purchase_date",
        minWidth: 150,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },

      {
        headerName: "Nhà cung cấp",
        field: "supplier_id",
        minWidth: 220,
        flex: 1,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return "";
          const label = supplierLabelById.get(params.value);
          return label ? `${label}` : String(params.value);
        },
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          const addr =
            supplierAddressById.get(
              (params.data as PurchaseOrder)?.supplier_id,
            ) ?? params.value;
          return addr ?? "";
        },
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 240,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      // {
      //   headerName: "Đính kèm",
      //   field: "attachments",
      //   minWidth: 180,
      //   filter: "agTextColumnFilter",
      // },
      {
        headerName: "Tổng tiền",
        field: "total_amount",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
      },
      // {
      //   headerName: "Dòng hàng",
      //   field: "items",
      //   minWidth: 220,
      //   flex: 1,
      //   filter: "agTextColumnFilter",
      //   valueFormatter: (params) => {
      //     if (Array.isArray(params.value)) {
      //       const total = params.value.reduce((sum, item) => {
      //         const amountCandidate =
      //           (item as any).amount ?? (item as any).final_amount;
      //         return sum + parseNumber(amountCandidate);
      //       }, 0);
      //       const firstDesc =
      //         params.value[0]?.product_name || params.value[0]?.description;
      //       return `${total.toLocaleString("vi-VN")} ₫${
      //         firstDesc ? ` - ${firstDesc}` : ""
      //       }`;
      //     }
      //     if (typeof params.value === "string") return params.value;
      //     return "";
      //   },
      // },
    ];
  }, [supplierLabelById]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
      editable: false,
    }),
    [],
  );

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const handleAdd = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.PURCHASE_DOCUMENT_CREATE);
  }, [navigate]);

  const handleEditRow = useCallback(
    (params: any) => {
      if (params?.data?.id) {
        navigate(
          `${ROUTES.ACCOUNTING.PURCHASE_DOCUMENT_DETAIL}/${params.data.id}`,
        );
      }
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<PurchaseOrder>) => {
      const items = sanitizeItems(event.data?.items, wmsItemById);
      setSelectedItems(items);
      setSelectedLabel(
        event.data?.purchase_code ??
          event.data?.supplier_name ??
          `Chứng từ ${event.data?.id ?? ""}`,
      );
    },
    [wmsItemById],
  );

  const resolveUnitLabel = useCallback(
    (item: PurchaseOrderItem) => {
      const fromItem = (item as any).unit_label;
      if (fromItem) return fromItem;
      const matched =
        wmsItemById.get(item.product_id as any) ||
        wmsItemById.get(item.unit_id as any) ||
        wmsItemById.get(String(item.product_id ?? "")) ||
        wmsItemById.get(String(item.unit_id ?? ""));
      const label =
        getUnitString((matched as any)?.unit) ||
        (matched?.unit_id ? String(matched.unit_id) : "");
      if (label) return label;
      return "";
    },
    [wmsItemById],
  );

  const handleDeleteRow = useCallback((row: PurchaseOrder) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deletePurchaseOrder.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa chứng từ mua hàng không thành công");
      },
    });
  }, [confirmRow, deletePurchaseOrder, removeRow]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          Chứng từ mua hàng
        </h1>
        <p className="text-sm text-gray-600">
          Danh sách chứng từ mua hàng. Chọn một dòng để xem chi tiết hàng hóa
          hoặc thêm mới để nhập chứng từ.
        </p>
      </div>

      <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-end">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 text-green-700" />
              Thêm chứng từ
            </button>
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Hàng hóa {selectedLabel ? `- ${selectedLabel}` : ""}
            </h3>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  {/* <th className="px-3 py-2 text-left font-semibold">Mã hàng</th> */}
                  <th className="px-3 py-2 text-left font-semibold">
                    Tên hàng
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">SL</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Đơn giá
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Thành tiền
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">VAT</th>
                  <th className="px-3 py-2 text-left font-semibold">Ghi chú</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length ? (
                  selectedItems.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      {/* <td className="px-3 py-2 text-gray-900">
                        {(item as any).product_id ?? ""}
                      </td> */}
                      <td className="px-3 py-2 text-gray-900">
                        {item.product_name ?? ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {(() => {
                          const qty = parseNumber(item.quantity).toLocaleString(
                            "vi-VN",
                          );
                          const unitLabel = resolveUnitLabel(item);
                          return unitLabel ? `${qty} ${unitLabel}` : qty;
                        })()}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.unit_price).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.amount).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item?.vat_amount !== undefined
                          ? parseNumber(item.vat_amount).toLocaleString(
                              "vi-VN",
                            ) + " ₫"
                          : item?.vat_rate !== undefined
                            ? `${item.vat_rate}%`
                            : ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.description ?? ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn chứng từ để xem chi tiết hàng hóa.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={620}
          autoHeight={false}
          defaultColDef={defaultColDef}
          pinnedLeftKeys={['purchase_code']}
          totalPages={purchaseOrderData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
        />

        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách chứng từ, vui lòng chờ giây lát."
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa chứng từ mua hàng?"
        description={`Bạn chắc chắn muốn xóa chứng từ "${confirmRow?.purchase_code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deletePurchaseOrder.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default PurchaseOrders;
